import { useCallback, useEffect, useRef, useState } from "react";
import { artworkPruefungenLaden, type ArtworkPruefung } from "../../../../api/artworkPruefungen.ts";
import { ApiFehler } from "../../../../api/basis.ts";
import { useMontiertRef } from "../../../../state/useMontiertRef.ts";
import { offenBeimStart, serverHinkt, type Fassung, type Urteil } from "./fassung.ts";
import type { Melder } from "./melder.ts";

/**
 * Der Prüfstand der Artworks einer Bestellung (Artwork-Abgleich, Spec „Anzeige“ - „Wie aktualisiert wird“).
 *
 * Der Prüfworker sendet KEINEN `documents`-Broadcast (der lädt die Positionsdokumente nicht nach, sondern merkt nur ein
 * Konflikt-Banner vor, `state/reduzierer.ts`). Darum fragt der Reiter selbst: solange mindestens ein Eintrag `laeuft`,
 * folgt der nächste Abruf `POLL_MS` nach dem vorigen - eine `setTimeout`-Kette ab der ANTWORT, kein `setInterval`: ein
 * hängender Abruf staut keine weiteren auf. Ist nichts mehr offen, hört die Kette auf.
 *
 * `schluessel` löst das Laden aus (`orderId` ebenso). Er ist für den Hook undurchsichtig; der Aufrufer bildet ihn aus dem,
 * was die Prüfung ungültig macht (`fassungsSchluessel`: Link, Version und Position der aktuellen Artworks - Umhängen reiht
 * eine neue Prüfung ein, ohne Link oder Version zu ändern). Ein leerer Schlüssel heißt „kein Artwork da“: es gibt nichts zu
 * zeigen und nichts abzurufen.
 *
 * `fassungen` sind die Fassungen, die der Aufrufer anzeigt. Gehört der Eintrag eines Artworks nicht zu seiner Fassung, entscheidet
 * `pruefungDerFassung` mit dem `urteil` dieses Hooks, was die Anzeige zeigt. Gefragt wird so lange nach:
 * - Echt: solange ein Eintrag `laeuft` und der letzte Abruf gelang, ohne Obergrenze - der Server arbeitet.
 * - Künstlich: solange der Server HINTERHERHINKT (Eintrag mit älterer Version als die Fassung: neue Version oder Umhängen, der
 *   Server hat noch nicht aufgeholt, oder das Laden scheiterte), höchstens `KUENSTLICH_MAX` Runden je Lauf; danach `aufgegeben`
 *   und die Anzeige zeigt „Stand veraltet“. Ebenso ein Lauf, der mit einem noch laufenden Eintrag aus dem Stand davor beginnt
 *   (Neustart des Laufs, etwa nach dem Schließen des Massenupload-Dialogs): bis zur ersten gelungenen Antwort gilt er als
 *   hinkend, also mit Obergrenze. Scheitern der Erstabruf und alle Runden danach, bleibt der alte Eintrag stehen: das Abzeichen
 *   zeigt weiter „Prüfung läuft“, ohne dass noch gefragt wird (die Meldung steht im Fehlerstapel), bis die Bestellung neu
 *   geöffnet wird.
 * - Gar nicht: ist der Server VORAUS (jüngere Version) oder weicht bei gleicher Version nur die Position ab, kennt der Aufrufer
 *   die Fassung nicht mehr (die Liste der Positionsdokumente ist veraltet); Nachfragen löst das nie auf, die Anzeige zeigt
 *   „Stand veraltet“, und zwar sofort nach einer gelungenen Antwort. Scheitert der erste Abruf eines Laufs, kennt der Hook die
 *   Richtung noch nicht und fragt (künstlich, wie oben) nach, bis ein Abruf gelingt - höchstens `KUENSTLICH_MAX` Runden.
 *
 * `melder` meldet das Scheitern des ersten Abrufs eines Laufs. Er kommt vom Aufrufer, der den Fehlerstapel selbst importiert
 * (Gründe in `melder.ts`).
 */
const POLL_MS = 5000;

/** Die Obergrenze für künstliche Runden je Lauf (60 s bei 5 s Takt) - dann geht der Server als „zu langsam“ durch. */
const KUENSTLICH_MAX = 12;

const KEINE: ReadonlyMap<string, ArtworkPruefung> = new Map();

/** Ein Lauf gehört zu einem Paar aus Bestellung und Schlüssel; der Wechsel eines von beiden löst ihn ab. */
interface Lauf {
  readonly orderId: string;
  readonly schluessel: string;
  readonly abbruch: AbortController;
  /** Der wartende Timer der Kette; höchstens einer. */
  timer: ReturnType<typeof setTimeout> | null;
  /** Zählt die Abrufe dieses Laufs: nur die Antwort des jüngsten wird übernommen (ein Poll darf `neuLaden` nicht überholen). */
  folge: number;
  /**
   * Warum die Kette zuletzt weiterfragte: der Server arbeitet (`echt`), er hinkt hinterher (`hinkt`) oder nichts davon (`ruhig`).
   * Ein neuer Lauf beginnt mit `hinkt`, wenn der Stand davor einen nicht passenden oder noch offenen Eintrag trug (`offenBeimStart`):
   * scheitert sein erster Abruf, steht dort noch dieser Eintrag und die Kette soll weiterfragen.
   */
  art: "echt" | "hinkt" | "ruhig";
  /** Die bisher verbrauchten künstlichen Runden (`KUENSTLICH_MAX`). */
  kuenstlich: number;
}

export function useArtworkPruefungen(
  orderId: string | null,
  schluessel: string,
  fassungen: readonly Fassung[],
  melder: Pick<Melder, "meldeFehler">,
): {
  nachLink: ReadonlyMap<string, ArtworkPruefung>;
  neuLaden: () => Promise<void>;
  urteil: Urteil;
} {
  // Der Stand trägt die Bestellung und den Schlüssel, zu denen er gehört: nach einem Wechsel zeigt der Hook sofort nichts von der
  // alten Bestellung, und `urteil` ist `offen`, bis die Antwort auf die neuen Fassungen da ist.
  const [stand, setStand] = useState<{
    orderId: string | null;
    schluessel: string;
    nachLink: ReadonlyMap<string, ArtworkPruefung>;
    aufgegeben: boolean;
  }>({ orderId: null, schluessel: "", nachLink: KEINE, aufgegeben: false });
  const montiertRef = useMontiertRef("passiv");
  const laufRef = useRef<Lauf | null>(null);
  // Die neuesten Werte für Abruf und Lauf, ohne dass ihr Wechsel allein einen neuen Lauf auslöst (das tut der Schlüssel).
  const fassungenRef = useRef(fassungen);
  fassungenRef.current = fassungen;
  const melderRef = useRef(melder);
  melderRef.current = melder;
  const standRef = useRef(stand);
  standRef.current = stand;

  /**
   * Ein Abruf des Laufs; bei laufender Prüfung hängt er den nächsten der Kette an. Eine Antwort eines abgelösten Laufs, nach
   * dem Abbau oder hinter einem jüngeren Abruf ändert nichts und plant nichts.
   *
   * Als Hintergrundabruf (Kette) meldet er sich nie: ein Aussetzer des Netzes soll den Fehlerstapel nicht alle fünf Sekunden
   * füllen. Er schreibt ins Log, behält den Stand und setzt die Kette fort (nur wenn zuletzt etwas lief). Sonst lehnt er bei
   * einem Fehler ab, und jeder Aufrufer meldet ihn mit dem Satz, der zu dem passt, was davor gelang.
   */
  const abrufen = useCallback(async function abrufen(lauf: Lauf, hintergrund: boolean): Promise<void> {
    if (lauf.timer !== null) {
      clearTimeout(lauf.timer);
      lauf.timer = null;
    }
    const folge = ++lauf.folge;
    const planen = (): void => {
      if (lauf.timer !== null) clearTimeout(lauf.timer);
      lauf.timer = setTimeout(() => {
        lauf.timer = null;
        void abrufen(lauf, true);
      }, POLL_MS);
    };
    const gueltig = (): boolean => !lauf.abbruch.signal.aborted && montiertRef.current && folge === lauf.folge;
    /** Ob die Kette weiterfragt; verbraucht dabei eine künstliche Runde. */
    const weiter = (): boolean => {
      if (lauf.art === "echt") return true;
      if (lauf.art !== "hinkt" || lauf.kuenstlich >= KUENSTLICH_MAX) return false;
      lauf.kuenstlich += 1;
      return true;
    };

    let liste: ArtworkPruefung[];
    try {
      liste = await artworkPruefungenLaden(lauf.orderId, { signal: lauf.abbruch.signal, hintergrund });
    } catch (ursache) {
      // Auch ein gescheitertes Neuladen von Hand lässt die Kette nicht sterben: der Abruf davor hat den Timer abgeräumt.
      if (gueltig()) {
        if (weiter()) planen();
        else if (lauf.art === "hinkt") setStand((alt) => ({ ...alt, schluessel: lauf.schluessel, aufgegeben: true }));
      }
      if (!hintergrund) throw ursache;
      if (gueltig()) console.error("Der Prüfstand der Artworks konnte nicht abgerufen werden:", ursache);
      return;
    }
    if (!gueltig()) return;
    const nachLink = new Map(liste.map((eintrag) => [eintrag.linkId, eintrag]));
    lauf.art = liste.some((eintrag) => eintrag.zustand === "laeuft")
      ? "echt"
      : serverHinkt(nachLink, fassungenRef.current) ? "hinkt" : "ruhig";
    const fragtWeiter = weiter();
    setStand({ orderId: lauf.orderId, schluessel: lauf.schluessel, nachLink, aufgegeben: lauf.art === "hinkt" && !fragtWeiter });
    if (fragtWeiter) planen();
  }, [montiertRef]);

  useEffect(() => {
    if (orderId === null || schluessel === "") {
      setStand((alt) => (alt.orderId === null ? alt : { orderId: null, schluessel: "", nachLink: KEINE, aufgegeben: false }));
      return;
    }
    // Der Stand davor gehört zu einer anderen Fassung, wenn das Artwork eine neue Version oder Position hat (oder wenn er bisher
    // „Stand veraltet“ zeigte und der Aufrufer neu laden lässt), oder er enthält einen Eintrag, der noch läuft (die Kette davor hat ihn
    // verfolgt): die Anzeige zeigt „Prüfung läuft“, und die Kette soll auch dann weiterfragen, wenn der erste Abruf dieses Laufs
    // scheitert. Die Richtung kennt der Hook erst nach der ersten Antwort; bis dahin zählt jeder Eintrag, der nicht passt oder noch
    // läuft, als hinkend (die Obergrenze gilt auch hier).
    const stehender = standRef.current.orderId === orderId ? standRef.current.nachLink : KEINE;
    const lauf: Lauf = {
      orderId,
      schluessel,
      abbruch: new AbortController(),
      timer: null,
      folge: 0,
      art: offenBeimStart(stehender, fassungenRef.current) ? "hinkt" : "ruhig",
      kuenstlich: 0,
    };
    laufRef.current = lauf;
    abrufen(lauf, false).catch((ursache: unknown) => {
      if (lauf.abbruch.signal.aborted) return;
      // 404: eine JTL-Bestellung ohne Hub-Zeile (`jtl-<n>`) kann keine Artworks tragen - der Normalfall, keine Meldung.
      if (ursache instanceof ApiFehler && ursache.status === 404) return;
      console.error("Der Prüfstand der Artworks konnte nicht geladen werden:", ursache);
      melderRef.current.meldeFehler(ursache, "Die Artwork-Prüfung konnte nicht geladen werden.");
    });
    return () => {
      lauf.abbruch.abort();
      if (lauf.timer !== null) clearTimeout(lauf.timer);
      lauf.timer = null;
      if (laufRef.current === lauf) laufRef.current = null;
    };
  }, [orderId, schluessel, abrufen]);

  /**
   * Lädt jetzt, ohne die Antwort der Kette abzuwarten (nach „Gesehen“ und „Erneut prüfen“). Lehnt ab, wenn das Laden
   * scheitert - der Aufrufer weiß, was davor gelang, und meldet es mit dem passenden Satz. Ohne Lauf (kein Artwork da) tut
   * sie nichts: eine neue Fassung löst über den Schlüssel selbst ein Laden aus. Ein Abbruch (Wechsel, Abbau) ist kein Fehler.
   */
  const neuLaden = useCallback(async (): Promise<void> => {
    const lauf = laufRef.current;
    if (lauf === null) return;
    try {
      await abrufen(lauf, false);
    } catch (ursache) {
      if (lauf.abbruch.signal.aborted) return;
      throw ursache;
    }
  }, [abrufen]);

  const gehoert = stand.orderId === orderId;
  const urteil: Urteil = gehoert && stand.schluessel === schluessel ? (stand.aufgegeben ? "aufgegeben" : "aktuell") : "offen";
  return { nachLink: gehoert ? stand.nachLink : KEINE, neuLaden, urteil };
}
